/** SafetySettings — workflow toggles and safety limits (PUT /api/settings/safety). */

import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle2 } from 'lucide-react'
import type { WorkflowSafetyConfig, ReviewMode } from '@dealcloser/shared'
import { DEFAULT_WORKFLOW_SAFETY } from '@dealcloser/shared'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PlanGate } from '@/components/plan-gate'
import { useApi } from '@/hooks/use-api'
import { usePlan } from '@/hooks/use-plan'

const safetyKeys = { config: ['settings', 'safety'] as const }

type ToggleRowProps = {
  readonly id: string; readonly checked: boolean; readonly onToggle: () => void
  readonly disabled: boolean; readonly label: string; readonly desc: string
}

function ToggleRow({ id, checked, onToggle, disabled, label, desc }: ToggleRowProps) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox id={id} checked={checked} onCheckedChange={onToggle} disabled={disabled} />
      <div>
        <label htmlFor={id} className="text-sm font-medium cursor-pointer">{label}</label>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </div>
  )
}

type RadioRowProps = {
  readonly id: string; readonly selected: boolean; readonly onSelect: () => void
  readonly disabled: boolean; readonly label: string; readonly desc: string
}

function RadioRow({ id, selected, onSelect, disabled, label, desc }: RadioRowProps) {
  return (
    <button
      type="button"
      id={id}
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <span
        aria-hidden
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
          selected ? 'border-primary' : 'border-muted-foreground/50'
        }`}
      >
        {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
      </span>
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{desc}</span>
      </span>
    </button>
  )
}

export default function SafetySettings() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const plan = usePlan()
  const [form, setForm] = useState<WorkflowSafetyConfig>(DEFAULT_WORKFLOW_SAFETY)
  const [savedSuccess, setSavedSuccess] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: safetyKeys.config,
    queryFn: async (): Promise<WorkflowSafetyConfig> => {
      const res = await apiFetch<WorkflowSafetyConfig>('/api/settings/safety')
      if (!res.success || !res.data) return DEFAULT_WORKFLOW_SAFETY
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async (config: WorkflowSafetyConfig): Promise<void> => {
      const res = await apiFetch('/api/settings/safety', { method: 'PUT', body: config })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: safetyKeys.config })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  function toggleField(field: keyof WorkflowSafetyConfig) {
    setForm((prev) => ({ ...prev, [field]: !prev[field] }))
  }

  function setNumberField(field: keyof WorkflowSafetyConfig, value: string) {
    const parsed = parseInt(value, 10)
    if (!Number.isNaN(parsed)) {
      setForm((prev) => ({ ...prev, [field]: parsed }))
    }
  }

  const isSaving = saveMutation.isPending
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : null

  function setReviewMode(mode: ReviewMode) {
    setForm((prev) => ({ ...prev, reviewMode: mode }))
  }

  const reviewMode: ReviewMode = form.reviewMode ?? 'none'

  return (
    <div className="space-y-6">
      {/* Manual Message Review */}
      <Card>
        <CardHeader>
          <CardTitle>Review Messages Manually</CardTitle>
          <CardDescription>
            Choose whether AI messages need your approval before they send.
            Held messages wait in your Inbox review queue and you get a
            notification for each one. Connection invites carry no text and
            always send automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2" role="radiogroup" aria-label="Review mode">
          <RadioRow
            id="review-mode-all"
            selected={reviewMode === 'all'}
            onSelect={() => setReviewMode('all')}
            disabled={isLoading || isSaving}
            label="Approve every message"
            desc="Nothing sends until you approve it. Every outreach message and AI reply waits for your OK."
          />
          <RadioRow
            id="review-mode-flagged"
            selected={reviewMode === 'flagged'}
            onSelect={() => setReviewMode('flagged')}
            disabled={isLoading || isSaving}
            label="Approve flagged messages only"
            desc="High scoring drafts send automatically. Drafts with a lower quality score wait for your approval. AI replies keep sending on their own."
          />
          <RadioRow
            id="review-mode-none"
            selected={reviewMode === 'none'}
            onSelect={() => setReviewMode('none')}
            disabled={isLoading || isSaving}
            label="No approvals needed"
            desc="Every message that passes quality checks sends on its own. This is the default."
          />
        </CardContent>
      </Card>

      {/* Workflow Toggles */}
      <Card>
        <CardHeader>
          <CardTitle>Workflow Toggles</CardTitle>
          <CardDescription>Enable or disable individual automation workflows.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow id="safety-warmup" checked={form.warmupEnabled}
            onToggle={() => toggleField('warmupEnabled')} disabled={isLoading || isSaving}
            label="Engagement Warmup" desc="View profiles and like posts before outreach" />
          <ToggleRow id="safety-ai-reply" checked={form.aiReplyEnabled}
            onToggle={() => toggleField('aiReplyEnabled')} disabled={isLoading || isSaving}
            label="AI Reply Handler" desc="Automatically classify and respond to incoming messages" />
          <PlanGate requiredPlan="pro" currentPlan={plan}>
            <ToggleRow id="safety-poster" checked={form.posterEnabled}
              onToggle={() => toggleField('posterEnabled')} disabled={isLoading || isSaving}
              label="LinkedIn Content Poster" desc="Auto-generate and publish LinkedIn posts" />
          </PlanGate>
          <PlanGate requiredPlan="pro" currentPlan={plan}>
            <ToggleRow id="safety-comment-dm" checked={form.commentDmEnabled}
              onToggle={() => toggleField('commentDmEnabled')} disabled={isLoading || isSaving}
              label="Comment-to-DM" desc="Auto-DM interested commenters on your posts" />
          </PlanGate>
        </CardContent>
      </Card>

      {/* Safety Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Safety Limits</CardTitle>
          <CardDescription>Control daily activity limits to keep your LinkedIn account safe.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="safety-max-connections">Max Daily Connection Requests</Label>
              <Input
                id="safety-max-connections"
                type="number"
                min={1}
                max={50}
                value={form.maxDailyConnections}
                onChange={(e) => setNumberField('maxDailyConnections', e.target.value)}
                disabled={isLoading || isSaving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="safety-max-views">Max Daily Profile Views</Label>
              <Input
                id="safety-max-views"
                type="number"
                min={1}
                max={100}
                value={form.maxDailyProfileViews}
                onChange={(e) => setNumberField('maxDailyProfileViews', e.target.value)}
                disabled={isLoading || isSaving}
              />
            </div>
          </div>

          <ToggleRow id="safety-weekend" checked={form.weekendEnabled}
            onToggle={() => toggleField('weekendEnabled')} disabled={isLoading || isSaving}
            label="Continue activity on weekends" desc="If off, outreach and automated posting pause Saturday and Sunday. Replies to incoming messages keep working." />

          <div className="space-y-4">
            <ToggleRow id="safety-rampup" checked={form.rampupEnabled}
              onToggle={() => toggleField('rampupEnabled')} disabled={isLoading || isSaving}
              label="Enable ramp-up period (gradually increase activity)"
              desc="Start with low volume and ramp to full limits over time" />
            {form.rampupEnabled && (
              <div className="ml-7 space-y-2">
                <Label htmlFor="safety-rampup-days">Ramp-Up Duration (days)</Label>
                <Input id="safety-rampup-days" type="number" min={1} max={30}
                  value={form.rampupDays} disabled={isLoading || isSaving}
                  onChange={(e) => setNumberField('rampupDays', e.target.value)}
                  className="max-w-[200px]" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="safety-off-start">Off-Hours Start (hour, 24h)</Label>
              <Input
                id="safety-off-start"
                type="number"
                min={0}
                max={23}
                value={form.offHoursStart}
                onChange={(e) => setNumberField('offHoursStart', e.target.value)}
                disabled={isLoading || isSaving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="safety-off-end">Off-Hours End (hour, 24h)</Label>
              <Input
                id="safety-off-end"
                type="number"
                min={0}
                max={23}
                value={form.offHoursEnd}
                onChange={(e) => setNumberField('offHoursEnd', e.target.value)}
                disabled={isLoading || isSaving}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={() => saveMutation.mutate(form)} disabled={isSaving || isLoading}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Safety Settings'
          )}
        </Button>
        {savedSuccess && (
          <div className="flex items-center gap-1.5 text-sm text-emerald-300 ">
            <CheckCircle2 className="h-4 w-4" />
            Saved successfully
          </div>
        )}
        {saveError && <p className="text-sm text-destructive">{saveError}</p>}
      </div>
    </div>
  )
}
